import { Form, Modal, Button as AntButton, Radio, Divider } from 'antd';
import { Bars } from 'react-loader-spinner';
import { CommonFormProps } from '.';
import { analytics } from 'src/services/analytics';
import { logger } from 'src/services/logger';
import { Logo } from '@insight-health-ai/roseate/components';

const FORM_TYPE = 'PHQ9';
type Props = CommonFormProps;

function PHQ9Form({ isOpen, appointment, loading, onSave, form }: Props) {
  const [phq9Form] = Form.useForm();
  const [additionalQuesForm] = Form.useForm();

  const formData = form.content as {
    title: string;
    description: string;
    questions: { title: string; options: { value: number; label: string }[] }[];
    additional_questions: {
      title: string;
      options: { value: number; label: string }[];
    }[];
  };

  const submit = async () => {
    try {
      analytics.track('Clicked Submit In PHQ9 Form', {
        appointment_id: appointment.appointment_id,
      });
      const values = (await phq9Form.validateFields()) as Record<
        string,
        number
      >;
      const additionalValues =
        (await additionalQuesForm.validateFields()) as Record<string, number>;

      const score = Object.entries(values).reduce((acc, [key, value]) => {
        acc = acc + value;
        return acc;
      }, 0);

      const questions = formData.questions;
      const response = questions.map(({ title, options }) => ({
        title,
        score: values[title],
        selected_option: options.find(
          (option) => option.value == values[title]
        ),
      }));

      const additionalResponse = (formData.additional_questions || []).map(
        ({ title, options }) => ({
          title,
          score: additionalValues[title],
          selected_option: options.find(
            (option) => option.value == additionalValues[title]
          ),
        })
      );
      await onSave({
        form_type: FORM_TYPE,
        response: {
          response,
          additional_response: additionalResponse,
          score,
        },
      });
    } catch (e) {
      logger.error('Error submitting PHQ9 form', {}, e as Error);
    }
  };

  if (!formData)
    return (
      <Modal open={isOpen} footer={null} closable centered>
        <div className="flex justify-center items-center h-full">
          <Bars
            height="25"
            width="25"
            color="#00D090"
            ariaLabel="bars-loading"
            wrapperStyle={{}}
            wrapperClass="pt-1 sm:pt-2"
            visible={true}
          />
        </div>
      </Modal>
    );

  const { questions } = formData;

  const renderQuestion = ({ title, options }: (typeof questions)[number]) => (
    <Form.Item
      key={title}
      name={title}
      label={<div className="text-md font-medium">{title}</div>}
      rules={[{ required: true, message: 'Please select an option' }]}
    >
      <Radio.Group
        options={options.map((option) => ({
          ...option,
          style: { display: 'flex' },
        }))}
      />
    </Form.Item>
  );

  return (
    <Modal
      open={isOpen}
      title={
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-center">
            <Logo className="h-8" />
          </div>
          <div className="flex flex-col gap-1">
            <div className="text-lg">{formData.title}</div>
            <div className="text-sm font-normal">{formData.description}</div>
          </div>
        </div>
      }
      footer={
        <div className="flex justify-end">
          <AntButton
            className="ih-button-primary"
            onClick={submit}
            loading={loading}
          >
            Submit
          </AntButton>
        </div>
      }
      closable={false}
      centered
    >
      <div className="flex flex-col mt-4">
        <Form form={phq9Form} layout="vertical">
          {questions.map(renderQuestion)}
        </Form>
        {formData.additional_questions?.length > 0 && (
          <>
            <Divider />
            <Form form={additionalQuesForm} layout="vertical">
              {formData.additional_questions.map(renderQuestion)}
            </Form>
          </>
        )}
      </div>
    </Modal>
  );
}

export { PHQ9Form };
